import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../articles/articlesService";
import {
  addPublishFileTypes,
  ArticlePreview,
  ArticlesAmountTypes,
  ArticleType,
  editArticleTypes,
  editMetadataTypes,
  putKeywordsTypes,
  searchByDateTypes,
  SearchPayload,
  trendArticlesType,
  updateKeywordsTypes,
} from "./articlesTypes";
import articlesService from "../articles/articlesService";
import { authortype, suggestedReviewerTypes } from "../authors/authorTypes";
import { SuccesType } from "../review/reviewTypes";

export interface ArticlesState {
  articles:
    | ArticleType[]
    | ArticlePreview[]
    | trendArticlesType[]
    | any[]
    | null;
  articles_amount: ArticlesAmountTypes | null;
  article: ArticleType | null;
  keywords: updateKeywordsTypes | null;
  articleAuthors: authortype[] | null;
  previousArticle: ArticleType | null;
  nextArticle: ArticleType | null;
  searchedArticles: SearchPayload | null;
  latestArticles: ArticlePreview[] | null;
  latestArticleLoading: boolean;
  suggestedReviewers: suggestedReviewerTypes[] | null;
  recentArticles: trendArticlesType[] | null;
  mostDownloadedArticles: trendArticlesType[] | null;
  mostViewedArticles: trendArticlesType[] | null;
  loading: boolean;
  recentArticlesLoading: boolean;
  mostDownloadedArticlesLoading: boolean;
  mostViewedArticlesLoading: boolean;
  error: string | null;
  articleError: boolean;
}

const initialState: ArticlesState = {
  articles: null,
  articles_amount: null,
  article: null,
  articleAuthors: null,
  previousArticle: null,
  nextArticle: null,
  latestArticles: null,
  latestArticleLoading: false,
  suggestedReviewers: null,
  searchedArticles: null,
  keywords: null,
  recentArticles: null,
  mostDownloadedArticles: null,
  mostViewedArticles: null,
  loading: false,
  recentArticlesLoading: false,
  mostDownloadedArticlesLoading: false,
  mostViewedArticlesLoading: false,
  error: null,
  articleError: false,
};

const getErrorMessage = (error: any): string => {
  if (typeof error === "string") return error;
  if (error?.message) return error.message;
  if (error?.response?.data?.message) return error.response.data.message;
  return "Error desconocido";
};

export const getArticles = createAsyncThunk<
  ArticleType[],
  void,
  { rejectValue: string }
>("articles/getArticles", async (_, { rejectWithValue }) => {
  try {
    return await authService.getArticles();
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const getArticlesAmountForStatus = createAsyncThunk<
  Record<string, number>,
  void,
  { rejectValue: string }
>("articles/getArticlesAmountForStatus", async (_, { rejectWithValue }) => {
  try {
    return await authService.getArticlesAmountForStatus();
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const getLatestArticlesByVolume = createAsyncThunk<
  ArticlePreview[],
  void,
  { rejectValue: string }
>("articles/getLatestArticlesByVolume", async (_, { rejectWithValue }) => {
  try {
    return await authService.getLatestArticlesByVolume();
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const getArticlesByVolume = createAsyncThunk<
  ArticlePreview[],
  string,
  { rejectValue: string }
>(
  "articles/getArticlesByVolume",
  async (volume_slug: string, { rejectWithValue }) => {
    try {
      return await authService.getArticlesByVolume(volume_slug);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const getRecentArticles = createAsyncThunk<
  trendArticlesType[],
  void,
  { rejectValue: string }
>("articles/getRecentArticles", async (_, { rejectWithValue }) => {
  try {
    return await authService.getRecentArticles();
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const getMostDownloadedArticles = createAsyncThunk<
  trendArticlesType[],
  void,
  { rejectValue: string }
>("articles/getMostDownloadedArticles", async (_, { rejectWithValue }) => {
  try {
    return await authService.getMostDownloadedArticles();
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const getMostViewedArticles = createAsyncThunk<
  trendArticlesType[],
  void,
  { rejectValue: string }
>("articles/getMostViewedArticles", async (_, { rejectWithValue }) => {
  try {
    return await authService.getMostViewedArticles();
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const getArticleBySlug = createAsyncThunk<
  ArticleType,
  string,
  { rejectValue: string }
>("articles/getArticleBySlug", async (slug: string, { rejectWithValue }) => {
  try {
    const article = await articlesService.getArticleBySlug(slug);
    const cleanField = (value: any) =>
      typeof value === "string" ? value.replace(/\\/g, "") : value;
    const cleanedArticle = Object.fromEntries(
      Object.entries(article as Record<string, any>).map(([k, v]) => [
        k,
        cleanField(v),
      ]),
    ) as ArticleType;
    return cleanedArticle;
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const getPublishedArticleBySlug = createAsyncThunk<
  ArticleType,
  string,
  { rejectValue: string }
>(
  "articles/getPublishedArticleBySlug",
  async (slug: string, { rejectWithValue }) => {
    try {
      return await articlesService.getPublishedArticleBySlug(slug);
    } catch (error: any) {
      console.error(error);
      return rejectWithValue(error);
    }
  },
);

export const getArticleById = createAsyncThunk<
  ArticleType,
  number,
  { rejectValue: string }
>("articles/getArticleById", async (id: number, { rejectWithValue }) => {
  try {
    return await authService.getArticleById(id);
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const getPreviousArticle = createAsyncThunk<
  ArticleType,
  string,
  { rejectValue: string }
>(
  "articles/getPreviousArticle",
  async (previousSlug: string, { rejectWithValue }) => {
    try {
      return await authService.getArticleBySlug(previousSlug);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const getNextArticle = createAsyncThunk<
  ArticleType,
  string,
  { rejectValue: string }
>("articles/getNextArticle", async (nextSlug: string, { rejectWithValue }) => {
  try {
    return await authService.getArticleBySlug(nextSlug);
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const registerDownload = createAsyncThunk<
  SuccesType,
  string,
  { rejectValue: string }
>(
  "articles/registerDownload",
  async (article_slug: string, { rejectWithValue }) => {
    try {
      return await articlesService.registerDownload(article_slug);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const registerDownloadWithoutLogin = createAsyncThunk<
  SuccesType,
  string,
  { rejectValue: string }
>(
  "articles/registerDownloadWithoutLogin",
  async (article_slug: string, { rejectWithValue }) => {
    try {
      return await articlesService.registerDownloadWithoutLogin(article_slug);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const searchQuery = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>("articles/searchQuery", async (query_text: string, { rejectWithValue }) => {
  try {
    return await articlesService.searchQuery(query_text);
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const editArticle = createAsyncThunk<
  any,
  editArticleTypes,
  { rejectValue: string }
>(
  "articles/editArticle",
  async (articleEditRequest: editArticleTypes, { rejectWithValue }) => {
    try {
      return await articlesService.editArticles(articleEditRequest);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const getArticleSuggestedReviewers = createAsyncThunk<
  suggestedReviewerTypes[],
  string,
  { rejectValue: string }
>(
  "articles/getArticleSuggestedReviewers",
  async (slug: string, { rejectWithValue }) => {
    try {
      return await articlesService.getArticleSuggestedReviewers(slug);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const addPublishFile = createAsyncThunk<
  SuccesType,
  addPublishFileTypes,
  { rejectValue: string }
>(
  "articles/addPublishFile",
  async (addPublishFileRequest: addPublishFileTypes, { rejectWithValue }) => {
    try {
      return await articlesService.addPublishFile(addPublishFileRequest);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);
export const addArticleCover = createAsyncThunk<
  string,
  { article_slug: string; file: FormData },
  { rejectValue: string }
>(
  "articles/addArticleCover",
  async ({ article_slug, file }, { rejectWithValue }) => {
    try {
      return await articlesService.addArticleCover({ article_slug, file });
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const addPublishXMLFile = createAsyncThunk<
  SuccesType,
  addPublishFileTypes,
  { rejectValue: string }
>(
  "articles/addPublishXMLFile",
  async (addPublishFileRequest: addPublishFileTypes, { rejectWithValue }) => {
    try {
      return await articlesService.addPublishXMLFile(addPublishFileRequest);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const editMetadata = createAsyncThunk<
  string,
  editMetadataTypes,
  { rejectValue: string }
>(
  "articles/editMetadata",
  async (editMetadataRequest: editMetadataTypes, { rejectWithValue }) => {
    try {
      return await authService.editMetadata(editMetadataRequest);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const searchByDate = createAsyncThunk<
  SearchPayload,
  searchByDateTypes,
  { rejectValue: string }
>(
  "articles/searchByDate",
  async (searchByDateRequest: searchByDateTypes, { rejectWithValue }) => {
    try {
      return await articlesService.searchByDate(searchByDateRequest);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const putKeywords = createAsyncThunk<
  updateKeywordsTypes,
  putKeywordsTypes,
  { rejectValue: string }
>(
  "articles/putKeywords",
  async (putKeywordsRequest: putKeywordsTypes, { rejectWithValue }) => {
    try {
      return await articlesService.putKeywords(putKeywordsRequest);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const getArticlesPreviewByVolume = createAsyncThunk<
  ArticlePreview[],
  string,
  { rejectValue: string }
>(
  "articles/getArticlesPreviewByVolume",
  async (volume_slug: string, { rejectWithValue }) => {
    try {
      return await authService.getArticlesPreviewByVolume(volume_slug);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const getLatestArticlesPreview = createAsyncThunk<
  ArticlePreview[],
  void,
  { rejectValue: string }
>("articles/getLatestArticlesPreview", async (_, { rejectWithValue }) => {
  try {
    return await authService.getLatestArticlesPreview();
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const getArticlesMetrics = createAsyncThunk<
  ArticleType[],
  void,
  { rejectValue: string }
>("articles/getArticlesMetrics", async (_, { rejectWithValue }) => {
  try {
    return await articlesService.getArticlesMetrics();
  } catch (error: any) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const generateDoi = createAsyncThunk<
  string,
  { article_slug: string; sufix: string },
  { rejectValue: string }
>(
  "articles/generateDoi",
  async ({ article_slug, sufix }, { rejectWithValue }) => {
    try {
      return await authService.generateDoi({ article_slug, sufix });
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

const articlesSlice = createSlice({
  name: "articles",
  initialState,
  reducers: {
    resetAuthors: (state) => {
      state.articleAuthors = null;
    },
    resetArticle: (state) => {
      state.article = null;
      state.previousArticle = null;
      state.nextArticle = null;
    },
    resetArticleAndAuthors: (state) => {
      state.article = null;
      state.articleAuthors = null;
      state.previousArticle = null;
      state.nextArticle = null;
    },
    resetError: (state) => {
      state.error = null;
    },
    resetLoading: (state) => {
      state.loading = false;
    },
    resetArticleError: (state) => {
      state.articleError = false;
      state.error = null;
      state.article = null;
    },
  },
  extraReducers: (builder) => {
    // getArticles
    builder
      .addCase(getArticles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getArticles.fulfilled, (state, action) => {
        state.loading = false;
        state.articles = action.payload;
        state.error = null;
      })
      .addCase(getArticles.rejected, (state, action) => {
        state.loading = false;
        state.articles = null;
        state.error = action.payload || "Error al obtener artículos";
      });

    // getArticlesAmountForStatus
    builder
      .addCase(getArticlesAmountForStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getArticlesAmountForStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.articles_amount = action.payload;
        state.error = null;
      })
      .addCase(getArticlesAmountForStatus.rejected, (state, action) => {
        state.loading = false;
        state.articles_amount = null;
        state.error = action.payload || "Error al obtener cantidades";
      });

    // getLatestArticlesByVolume
    builder
      .addCase(getLatestArticlesByVolume.pending, (state) => {
        state.latestArticleLoading = true;
        state.latestArticles = null;
        state.error = null;
      })
      .addCase(getLatestArticlesByVolume.fulfilled, (state, action) => {
        state.latestArticleLoading = false;
        state.latestArticles = action.payload;
        state.error = null;
      })
      .addCase(getLatestArticlesByVolume.rejected, (state, action) => {
        state.latestArticleLoading = false;
        state.latestArticles = null;
        state.error = action.payload || "Error al obtener últimos artículos";
      });

    // getArticlesByVolume
    builder
      .addCase(getArticlesByVolume.pending, (state) => {
        state.latestArticleLoading = true;
        state.articles = null;
        state.latestArticles = null;
        state.error = null;
      })
      .addCase(getArticlesByVolume.fulfilled, (state, action) => {
        state.latestArticleLoading = false;
        state.articles = action.payload;
        state.latestArticles = action.payload;
        state.error = null;
      })
      .addCase(getArticlesByVolume.rejected, (state, action) => {
        state.latestArticleLoading = false;
        state.articles = null;
        state.latestArticles = null;
        state.error =
          action.payload || "Error al obtener artículos por volumen";
      });

    // getRecentArticles
    builder
      .addCase(getRecentArticles.pending, (state) => {
        state.recentArticlesLoading = true;
        state.error = null;
      })
      .addCase(getRecentArticles.fulfilled, (state, action) => {
        state.recentArticlesLoading = false;
        state.recentArticles = action.payload;
        state.error = null;
      })
      .addCase(getRecentArticles.rejected, (state, action) => {
        state.recentArticlesLoading = false;
        state.recentArticles = null;
        state.error = action.payload || "Error al obtener artículos recientes";
      });

    // getMostDownloadedArticles
    builder
      .addCase(getMostDownloadedArticles.pending, (state) => {
        state.mostDownloadedArticlesLoading = true;
        state.error = null;
      })
      .addCase(getMostDownloadedArticles.fulfilled, (state, action) => {
        state.mostDownloadedArticlesLoading = false;
        state.mostDownloadedArticles = action.payload;
        state.error = null;
      })
      .addCase(getMostDownloadedArticles.rejected, (state, action) => {
        state.mostDownloadedArticlesLoading = false;
        state.mostDownloadedArticles = null;
        state.error =
          action.payload || "Error al obtener artículos más descargados";
      });

    // getMostViewedArticles
    builder
      .addCase(getMostViewedArticles.pending, (state) => {
        state.mostViewedArticlesLoading = true;
        state.error = null;
      })
      .addCase(getMostViewedArticles.fulfilled, (state, action) => {
        state.mostViewedArticlesLoading = false;
        state.mostViewedArticles = action.payload;
        state.error = null;
      })
      .addCase(getMostViewedArticles.rejected, (state, action) => {
        state.mostViewedArticlesLoading = false;
        state.mostViewedArticles = null;
        state.error = action.payload || "Error al obtener artículos más vistos";
      });

    // getArticleBySlug
    builder
      .addCase(getArticleBySlug.pending, (state) => {
        state.loading = true;
        state.article = null;
        state.previousArticle = null;
        state.nextArticle = null;
        state.error = null;
        state.articleError = false;
      })
      .addCase(getArticleBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.article = action.payload;
        state.error = null;
        state.articleError = false;
      })
      .addCase(getArticleBySlug.rejected, (state, action) => {
        state.loading = false;
        state.article = null;
        state.error = action.payload || "Error al obtener artículo";
        state.articleError = true;
      });

    builder
      .addCase(getPublishedArticleBySlug.pending, (state) => {
        state.loading = true;
        state.article = null;
        state.error = null;
      })
      .addCase(getPublishedArticleBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.article = action.payload;
        state.error = null;
      })
      .addCase(getPublishedArticleBySlug.rejected, (state, action) => {
        state.loading = false;
        state.article = null;
        state.error = action.payload || "Error al obtener artículo publicado";
      });

    // getArticleById
    builder
      .addCase(getArticleById.pending, (state) => {
        state.loading = true;
        state.article = null;
        state.error = null;
      })
      .addCase(getArticleById.fulfilled, (state, action) => {
        state.loading = false;
        state.article = action.payload;
        state.error = null;
      })
      .addCase(getArticleById.rejected, (state, action) => {
        state.loading = false;
        state.article = null;
        state.error = action.payload || "Error al obtener artículo por ID";
      });

    // getPreviousArticle
    builder
      .addCase(getPreviousArticle.pending, (state) => {
        state.loading = true;
        state.previousArticle = null;
        state.error = null;
      })
      .addCase(getPreviousArticle.fulfilled, (state, action) => {
        state.loading = false;
        state.previousArticle = action.payload;
        state.error = null;
      })
      .addCase(getPreviousArticle.rejected, (state, action) => {
        state.loading = false;
        state.previousArticle = null;
        state.error = action.payload || "Error al obtener artículo anterior";
      });

    // getNextArticle
    builder
      .addCase(getNextArticle.pending, (state) => {
        state.loading = true;
        state.nextArticle = null;
        state.error = null;
      })
      .addCase(getNextArticle.fulfilled, (state, action) => {
        state.loading = false;
        state.nextArticle = action.payload;
        state.error = null;
      })
      .addCase(getNextArticle.rejected, (state, action) => {
        state.loading = false;
        state.nextArticle = null;
        state.error = action.payload || "Error al obtener artículo siguiente";
      });

    // registerDownload
    builder
      .addCase(registerDownload.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerDownload.fulfilled, (state) => {
        state.loading = false;
        if (state.article) {
          state.article.downloads = (state.article.downloads || 0) + 1;
        }
        state.error = null;
      })
      .addCase(registerDownload.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al registrar descarga";
      });

    // registerDownloadWithoutLogin
    builder
      .addCase(registerDownloadWithoutLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerDownloadWithoutLogin.fulfilled, (state) => {
        state.loading = false;
        if (state.article) {
          state.article.downloads = (state.article.downloads || 0) + 1;
        }
        state.error = null;
      })
      .addCase(registerDownloadWithoutLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al registrar descarga sin login";
      });

    // searchQuery
    builder
      .addCase(searchQuery.pending, (state) => {
        state.loading = true;
        state.searchedArticles = null;
        state.error = null;
      })
      .addCase(searchQuery.fulfilled, (state, action) => {
        state.loading = false;
        state.searchedArticles = action.payload;
        state.articles = action.payload;
        state.error = null;
      })
      .addCase(searchQuery.rejected, (state, action) => {
        state.loading = false;
        state.searchedArticles = null;
        state.error = action.payload || "Error en la búsqueda";
      });

    // editArticle
    builder
      .addCase(editArticle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editArticle.fulfilled, (state, action) => {
        state.loading = false;
        // Actualizar el artículo si existe
        if (state.article && action.payload) {
          state.article = { ...state.article, ...action.payload };
        }
        state.error = null;
      })
      .addCase(editArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al editar artículo";
      });

    // getArticleSuggestedReviewers
    builder
      .addCase(getArticleSuggestedReviewers.pending, (state) => {
        state.loading = true;
        state.suggestedReviewers = null;
        state.error = null;
      })
      .addCase(getArticleSuggestedReviewers.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestedReviewers = action.payload;
        state.error = null;
      })
      .addCase(getArticleSuggestedReviewers.rejected, (state, action) => {
        state.loading = false;
        state.suggestedReviewers = null;
        state.error = action.payload || "Error al obtener revisores sugeridos";
      });

    // addPublishFile
    builder
      .addCase(addPublishFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPublishFile.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(addPublishFile.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Error al agregar archivo de publicación";
      });

    // editMetadata
    builder
      .addCase(editMetadata.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editMetadata.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(editMetadata.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al editar metadatos";
      });

    // searchByDate
    builder
      .addCase(searchByDate.pending, (state) => {
        state.loading = true;
        state.searchedArticles = null;
        state.error = null;
      })
      .addCase(searchByDate.fulfilled, (state, action) => {
        state.loading = false;
        state.searchedArticles = action.payload;
        state.articles = action.payload;
        state.error = null;
      })
      .addCase(searchByDate.rejected, (state, action) => {
        state.loading = false;
        state.searchedArticles = null;
        state.error = action.payload || "Error en búsqueda por fecha";
      });

    // putKeywords
    builder
      .addCase(putKeywords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(putKeywords.fulfilled, (state, action) => {
        state.loading = false;
        state.keywords = action.payload;
        state.error = null;
      })
      .addCase(putKeywords.rejected, (state, action) => {
        state.loading = false;
        state.keywords = null;
        state.error = action.payload || "Error al actualizar keywords";
      });

    // getArticlesPreviewByVolume
    builder
      .addCase(getArticlesPreviewByVolume.pending, (state) => {
        state.latestArticleLoading = true;
        state.latestArticles = null;
        state.error = null;
      })
      .addCase(getArticlesPreviewByVolume.fulfilled, (state, action) => {
        state.latestArticleLoading = false;
        state.latestArticles = action.payload;
        state.articles = action.payload;
        state.error = null;
      })
      .addCase(getArticlesPreviewByVolume.rejected, (state, action) => {
        state.latestArticleLoading = false;
        state.latestArticles = null;
        state.error =
          action.payload || "Error al obtener vista previa por volumen";
      });

    // getLatestArticlesPreview
    builder
      .addCase(getLatestArticlesPreview.pending, (state) => {
        state.latestArticleLoading = true;
        state.latestArticles = null;
        state.error = null;
      })
      .addCase(getLatestArticlesPreview.fulfilled, (state, action) => {
        state.latestArticleLoading = false;
        state.latestArticles = action.payload;
        state.error = null;
      })
      .addCase(getLatestArticlesPreview.rejected, (state, action) => {
        state.latestArticleLoading = false;
        state.latestArticles = null;
        state.error =
          action.payload ||
          "Error al obtener vista previa de últimos artículos";
      });

    // getArticlesMetrics
    builder
      .addCase(getArticlesMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getArticlesMetrics.fulfilled, (state, action) => {
        state.loading = false;
        state.articles = action.payload;
        state.error = null;
      })
      .addCase(getArticlesMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al obtener métricas";
      });

    // generateDoi
    builder
      .addCase(generateDoi.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateDoi.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(generateDoi.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al generar DOI";
      });
  },
});

export const {
  resetAuthors,
  resetArticleError,
  resetArticle,
  resetArticleAndAuthors,
  resetError,
  resetLoading,
} = articlesSlice.actions;
export default articlesSlice.reducer;
